// GS Mandal — Public Vendor Empanelment registration wizard.
// Path: /vendors/register
// Three steps: Applicant details → Organization details → Review & Submit.
// PDFs must have alphanumeric-only filenames (e.g. PANcopy.pdf).

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Send, CheckCircle2, ShieldCheck, Building2, Upload, X, FileText, Info } from "lucide-react";
import { INDIAN_STATES, citiesForState } from "@/data/indianStatesCities";

const APPLICANT_TYPES = ["Individual", "Proprietorship", "Partnership", "LLP", "Private Ltd.", "Public Ltd.", "Trust", "Society", "Others"];
const EMPLOYEE_RANGES = ["1-10", "11-50", "51-100", "101-300", "Above 300"];
const TURNOVER_RANGES = [
  "Below ₹1 Crore",
  "₹1 – 5 Crore",
  "₹5 – 10 Crore",
  "₹10 – 25 Crore",
  "₹25 – 50 Crore",
  "Above ₹50 Crore",
];

const FILENAME_RE = /^[A-Za-z0-9]+\.pdf$/i;
const MAX_KB = 500;

// -----------------------------------------------------------------------------
// Small reusable form primitives
// -----------------------------------------------------------------------------
const inputCls = "w-full bg-white border border-[#E9E2D2] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5C63]";

function Field({ label, required, children, hint }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold mb-1.5">
        {label}{required ? " *" : ""}
      </div>
      {children}
      {hint && <div className="text-[10px] text-[#8a8478] mt-1">{hint}</div>}
    </label>
  );
}

function PdfUpload({ testid, label, hint, file, onChange, required }) {
  const onPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!FILENAME_RE.test(f.name)) {
      toast.error(`${label}: filename must be alphanumeric only, ending in .pdf (e.g. PANcopy.pdf)`);
      return;
    }
    if (f.type !== "application/pdf") {
      toast.error(`${label}: only PDF files are allowed`);
      return;
    }
    if (f.size === 0) {
      toast.error(`${label}: the file is empty (0 KB). Please re-save the PDF and upload again.`);
      return;
    }
    if (f.size > MAX_KB * 1024) {
      toast.error(`${label}: file must be ${MAX_KB} KB or smaller`);
      return;
    }
    onChange(f);
  };
  return (
    <div className="border border-[#E9E2D2] rounded-lg p-3 bg-[#FAFAF7]" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold">{label}{required ? " *" : ""}</div>
          <div className="text-[10px] text-[#8a8478] mt-0.5">{hint} · PDF only · max {MAX_KB} KB · filename alphanumeric only</div>
        </div>
        <label className="inline-flex items-center gap-1.5 bg-[#0D5C63] hover:bg-[#083E43] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md cursor-pointer">
          <Upload size={12} /> {file ? "Replace" : "Choose PDF"}
          <input type="file" accept="application/pdf" hidden onChange={onPick} data-testid={`${testid}-input`} />
        </label>
      </div>
      {file && (
        <div className="mt-2 flex items-center justify-between gap-3 bg-white rounded border border-[#E9E2D2] px-3 py-2">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={14} className="text-[#0D5C63] shrink-0" />
            <div className="text-xs text-[#1A1A1A] truncate" title={file.name}>{file.name}</div>
            <div className="text-[10px] text-[#8a8478] shrink-0">{Math.ceil(file.size / 1024)} KB</div>
          </div>
          <button type="button" onClick={() => onChange(null)} className="text-red-700 hover:bg-red-50 rounded p-1" aria-label="Remove" data-testid={`${testid}-remove`}><X size={13} /></button>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Page hero + progress bar
// -----------------------------------------------------------------------------
function PageHero() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-r from-[#0D5C63] via-[#0D5C63] to-[#083E43] text-white">
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 90% 10%, #F26419 0, transparent 45%), radial-gradient(circle at 10% 90%, #D90368 0, transparent 45%)" }} />
      <div className="relative max-w-6xl mx-auto px-5 py-14">
        <div className="trust-eyebrow text-[10px] text-[#F26419] font-bold">Empanelment</div>
        <h1 className="trust-serif text-4xl sm:text-5xl mt-2 leading-tight">Vendor <span className="text-[#F26419]">Registration</span></h1>
        <p className="mt-4 max-w-3xl text-white/85 leading-relaxed">
          Register your firm to be considered for GS Mandal empanelment across construction, architecture, IT, catering, medical supply and allied services. Complete both steps carefully — you will be asked to upload your PAN Card and GST Registration Certificate (PDF, ≤ 500 KB each) in Step 2. Once your application is approved, you will receive login credentials by email to submit the remaining compliance documents through the Vendor Portal.
        </p>
      </div>
    </section>
  );
}

function Progress({ step }) {
  const items = [
    { n: 1, label: "Applicant Details" },
    { n: 2, label: "Organization Details" },
    { n: 3, label: "Review & Submit" },
  ];
  return (
    <div className="max-w-6xl mx-auto px-5 -mt-8 relative z-10">
      <div className="bg-white border border-[#E9E2D2] rounded-2xl shadow-md px-5 py-4 flex items-center justify-between gap-3" data-testid="vendor-progress">
        {items.map((it, i) => {
          const active = step === it.n;
          const done = step > it.n;
          return (
            <div key={it.n} className="flex items-center gap-3 flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${done ? "bg-[#0D5C63] text-white" : active ? "bg-[#F26419] text-white ring-4 ring-[#F26419]/20" : "bg-[#F0EBE0] text-[#5B5B5B]"}`}>
                {done ? <CheckCircle2 size={14} /> : it.n}
              </div>
              <div className={`text-xs font-semibold uppercase tracking-wider ${active ? "text-[#0D5C63]" : done ? "text-[#0D5C63]" : "text-[#5B5B5B]"}`}>{it.label}</div>
              {i < items.length - 1 && <div className={`h-px flex-1 ${done ? "bg-[#0D5C63]" : "bg-[#E9E2D2]"}`} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main wizard component
// -----------------------------------------------------------------------------
export default function TrustVendorRegister() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [masters, setMasters] = useState({ types: [], categories: [], categories_by_type: {} });

  // ---- Step 1 state ----
  const [firm, setFirm] = useState("");
  const [applicantType, setApplicantType] = useState("");
  const [regAddress, setRegAddress] = useState("");
  const [commAddress, setCommAddress] = useState("");
  const [sameAddr, setSameAddr] = useState(false);
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [pin, setPin] = useState("");
  const [officePhone, setOfficePhone] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [branchDetails, setBranchDetails] = useState("");
  const [website, setWebsite] = useState("");

  // ---- Step 2 state ----
  const [companyType, setCompanyType] = useState("");
  const [companyCategories, setCompanyCategories] = useState([]);
  // "Council of Architecture Registration" and "Institute of Engineers
  // Membership" are only meaningful for civil-construction / infrastructure
  // firms. Enable those fields when either the Type of Company OR any
  // selected Category matches the "civil construction & infrastructure"
  // keyword (matching is case- and separator-insensitive).
  const isCivilInfra = (() => {
    // Enable when Type of Company OR any selected Category mentions civil
    // works / construction / infrastructure. Matching is case-insensitive
    // and tolerant of separators.
    const re = /\bcivil\b|infrastructure/i;
    if (re.test(companyType)) return true;
    return (companyCategories || []).some((c) => re.test(c || ""));
  })();
  const [yearEstablished, setYearEstablished] = useState("");
  const [regNumber, setRegNumber] = useState("");
  const [regDate, setRegDate] = useState("");
  const [pan, setPan] = useState("");
  const [gst, setGst] = useState("");
  const [coa, setCoa] = useState("");
  const [ieMembership, setIeMembership] = useState("");
  const [otherReg, setOtherReg] = useState("");
  const [longDesc, setLongDesc] = useState("");
  const [empRange, setEmpRange] = useState("");
  const [annualTurnover, setAnnualTurnover] = useState("");
  const [panFile, setPanFile] = useState(null);
  const [gstFile, setGstFile] = useState(null);
  const [gstNotApplicable, setGstNotApplicable] = useState(false);

  // ---- Step 3 declaration (single combined consent) ----
  const [declared, setDeclared] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/vendors/masters");
        setMasters({
          types: data?.types || [],
          categories: data?.categories || [],
          categories_by_type: data?.categories_by_type || {},
        });
      } catch { /* silent */ }
    })();
  }, []);

  const cities = useMemo(() => citiesForState(state), [state]);
  useEffect(() => { if (sameAddr) setCommAddress(regAddress); }, [sameAddr, regAddress]);

  // ---- Validation per step ----
  const validateStep1 = () => {
    if (!firm.trim()) return "Applicant/Firm name is required";
    if (!APPLICANT_TYPES.includes(applicantType)) return "Please select Type of Applicant";
    if (!regAddress.trim()) return "Registered address is required";
    if (!commAddress.trim()) return "Communication address is required";
    if (!state.trim() || !city.trim()) return "State and City are required";
    if (!/^\d{6}$/.test(pin.trim())) return "PIN code must be exactly 6 digits";
    if (!mobile.trim()) return "Mobile number is required";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return "Please enter a valid email";
    return "";
  };
  const validateStep2 = () => {
    if (!companyType.trim()) return "Type of Company is required";
    if (!companyCategories || companyCategories.length === 0) return "Please select at least one Category of Company";
    const yr = parseInt(yearEstablished, 10);
    if (isNaN(yr) || yr < 1800 || yr > new Date().getFullYear()) return "Please enter a valid 4-digit Year of Establishment";
    if (!regNumber.trim()) return "Registration/Incorporation number is required";
    if (!regDate.trim()) return "Registration/Incorporation date is required";
    if (!pan.trim()) return "PAN number is required";
    if (!EMPLOYEE_RANGES.includes(empRange)) return "Please select No. of Employees";
    if (!TURNOVER_RANGES.includes(annualTurnover)) return "Please select Annual Turnover range";
    if (!panFile) return "Please attach the PAN Card copy (PDF)";
    if (!gstFile) return gstNotApplicable
      ? "Please attach the GST-not-applicable declaration (PDF)"
      : "Please attach the GST Registration certificate (PDF)";
    return "";
  };
  const validateConsents = () => declared ? "" : "Please accept the declaration before submitting";

  const goNext = () => {
    let err = "";
    if (step === 1) err = validateStep1();
    else if (step === 2) err = validateStep2();
    if (err) { toast.error(err); return; }
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    const err = validateStep1() || validateStep2() || validateConsents();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      // Step 1
      fd.append("applicant_firm_name", firm.trim());
      fd.append("applicant_type", applicantType);
      fd.append("registered_address", regAddress.trim());
      fd.append("communication_address", commAddress.trim());
      fd.append("state", state);
      fd.append("city", city);
      fd.append("pin_code", pin.trim());
      fd.append("office_phone", officePhone.trim());
      fd.append("mobile", mobile.trim());
      fd.append("email", email.trim());
      fd.append("branch_office_details", branchDetails.trim());
      fd.append("website", website.trim());
      // Step 2
      fd.append("company_type", companyType);
      fd.append("company_categories", JSON.stringify(companyCategories));
      fd.append("year_of_establishment", String(parseInt(yearEstablished, 10)));
      fd.append("registration_number", regNumber.trim());
      fd.append("registration_date", regDate);
      fd.append("pan_number", pan.trim().toUpperCase());
      fd.append("gst_number", gst.trim().toUpperCase());
      fd.append("coa_registration", coa.trim());
      fd.append("ie_membership", ieMembership.trim());
      fd.append("other_professional_registration", otherReg.trim());
      fd.append("long_description", longDesc.trim());
      fd.append("employees_range", empRange);
      fd.append("annual_turnover", annualTurnover);
      fd.append("gst_not_applicable", gstNotApplicable ? "true" : "false");
      // Consents
      fd.append("consent_information_correct", "true");
      fd.append("consent_verify", "true");
      fd.append("consent_false_cancellation", "true");
      fd.append("consent_gsm_decision_final", "true");
      // Required attachments
      fd.append("pan_card", panFile, panFile.name);
      fd.append("gst_certificate", gstFile, gstFile.name);

      await api.post("/public/vendors/register", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Thank you — your empanelment application has been submitted for review");
      navigate("/vendors/thank-you");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : `Submission failed${e?.response?.status ? ` (HTTP ${e.response.status})` : " (network error)"}. Please try again.`);
    } finally { setSaving(false); }
  };

  // -----------------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------------
  return (
    <div data-testid="trust-vendor-register-page">
      <PageHero />
      <Progress step={step} />

      <section className="max-w-6xl mx-auto px-5 py-12">
        {step === 1 && (
          <div className="bg-white border border-[#E9E2D2] rounded-2xl shadow-sm p-6 md:p-8" data-testid="vendor-step-1">
            <div className="trust-eyebrow text-[10px] text-[#D90368] font-bold">Step 1</div>
            <h2 className="trust-serif text-2xl text-[#1A1A1A] mt-1">Applicant Details</h2>

            <div
              className="mt-4 flex items-start gap-3 bg-[#FFF7ED] border-l-4 border-[#F26419] rounded-r-lg px-4 py-3"
              data-testid="vendor-docs-notice"
              role="note"
            >
              <Info size={18} className="text-[#F26419] shrink-0 mt-0.5" />
              <div className="text-sm text-[#3E3E3E] leading-relaxed">
                <span className="font-semibold text-[#1A1A1A]">Important — please keep two documents ready before you begin:</span>
                <span className="block mt-1">
                  A scanned copy of your <b>PAN Card</b> and your <b>GST Registration Certificate</b> (or a signed
                  &quot;GST not applicable&quot; declaration on letterhead) are required to complete registration in Step&nbsp;2.
                </span>
                <ul className="mt-1.5 pl-4 list-disc text-[13px] marker:text-[#F26419]">
                  <li>Format: <b>PDF only</b></li>
                  <li>Maximum size: <b>500&nbsp;KB per file</b></li>
                  <li>Filename must contain <b>only letters and numbers</b> — no spaces or special characters (for example, <span className="font-mono text-[12px]">PANcopy.pdf</span> is valid; <span className="font-mono text-[12px]">PAN Card (2024).pdf</span> is not).</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-4">
              <Field label="Applicant / Firm Name" required>
                <input type="text" data-testid="vendor-firm" className={inputCls} value={firm} onChange={(e) => setFirm(e.target.value)} maxLength={240} />
              </Field>
              <Field label="Type of Applicant" required>
                <select data-testid="vendor-applicant-type" className={inputCls} value={applicantType} onChange={(e) => setApplicantType(e.target.value)}>
                  <option value="">— Select —</option>
                  {APPLICANT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Registered Address" required>
                <textarea data-testid="vendor-reg-address" className={`${inputCls} min-h-[80px]`} value={regAddress} onChange={(e) => setRegAddress(e.target.value)} maxLength={800} />
              </Field>
              <Field label="Communication Address" required hint={<span className="text-sm md:text-base text-[#1A1A1A] font-medium">Same as Registered Address? <button type="button" className="text-[#0D5C63] font-bold underline underline-offset-2" onClick={() => setSameAddr((v) => !v)}>{sameAddr ? "No" : "Yes"}</button></span>}>
                <textarea data-testid="vendor-comm-address" className={`${inputCls} min-h-[80px] ${sameAddr ? "bg-[#F5F1E8]" : ""}`} value={commAddress} disabled={sameAddr} onChange={(e) => setCommAddress(e.target.value)} maxLength={800} />
              </Field>
              <Field label="State" required>
                <select data-testid="vendor-state" className={inputCls} value={state} onChange={(e) => { setState(e.target.value); setCity(""); }}>
                  <option value="">— Select State —</option>
                  {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="City" required>
                <select data-testid="vendor-city" className={inputCls} value={city} disabled={!state} onChange={(e) => setCity(e.target.value)}>
                  <option value="">{state ? "— Select City —" : "Select state first"}</option>
                  {cities.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="PIN Code" required>
                <input type="text" inputMode="numeric" pattern="\d{6}" data-testid="vendor-pin" className={inputCls} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6 digits" maxLength={6} />
              </Field>
              <Field label="Office Phone">
                <input type="tel" data-testid="vendor-office-phone" className={inputCls} value={officePhone} onChange={(e) => setOfficePhone(e.target.value)} maxLength={40} />
              </Field>
              <Field label="Mobile Number" required>
                <input type="tel" data-testid="vendor-mobile" className={inputCls} value={mobile} onChange={(e) => setMobile(e.target.value)} maxLength={40} />
              </Field>
              <Field label="Email Address" required>
                <input type="email" data-testid="vendor-email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} />
              </Field>
              <Field label="Website">
                <input type="url" data-testid="vendor-website" className={inputCls} value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://…" maxLength={200} />
              </Field>
              <div className="md:col-span-2">
                <Field label="Branch Office Details (if any)">
                  <textarea data-testid="vendor-branch" className={`${inputCls} min-h-[80px]`} value={branchDetails} onChange={(e) => setBranchDetails(e.target.value)} maxLength={2000} placeholder="City-wise list of branches, if applicable" />
                </Field>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white border border-[#E9E2D2] rounded-2xl shadow-sm p-6 md:p-8" data-testid="vendor-step-2">
            <div className="trust-eyebrow text-[10px] text-[#D90368] font-bold">Step 2</div>
            <h2 className="trust-serif text-2xl text-[#1A1A1A] mt-1">Organization Details</h2>

            <div className="mt-6 grid md:grid-cols-2 gap-4">
              <Field label="Type of Company" required>
                <select data-testid="vendor-company-type" className={inputCls} value={companyType} onChange={(e) => { setCompanyType(e.target.value); setCompanyCategories([]); }}>
                  <option value="">— Select —</option>
                  {masters.types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <div className="md:col-span-2">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold mb-1.5">Category of Company *</div>
                {!companyType ? (
                  <div className="bg-[#FAFAF7] border border-dashed border-[#E9E2D2] rounded-lg px-4 py-3 text-xs text-[#8a8478]" data-testid="vendor-category-empty">
                    Select a Type of Company above to see available categories.
                  </div>
                ) : (masters.categories_by_type[companyType] || []).length === 0 ? (
                  <div className="bg-[#FAFAF7] border border-dashed border-[#E9E2D2] rounded-lg px-4 py-3 text-xs text-[#8a8478]" data-testid="vendor-category-none">
                    No categories configured under "{companyType}" yet.
                  </div>
                ) : (
                  <div className="bg-[#FAFAF7] border border-[#E9E2D2] rounded-lg p-4" data-testid="vendor-category-checkboxes">
                    <div className="text-[10px] text-[#8a8478] mb-3">Select all that apply — you can pick multiple categories.</div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {(masters.categories_by_type[companyType] || []).map((c) => {
                        const checked = companyCategories.includes(c);
                        return (
                          <label key={c} className={`flex items-start gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer transition-colors ${checked ? "bg-[#0D5C63]/8 border-[#0D5C63]" : "bg-white border-[#E9E2D2] hover:border-[#0D5C63]/40"}`}>
                            <input
                              type="checkbox"
                              className="mt-0.5 h-4 w-4 accent-[#0D5C63]"
                              checked={checked}
                              data-testid={`vendor-category-${c}`}
                              onChange={(e) => setCompanyCategories((cur) => e.target.checked ? [...cur, c] : cur.filter((x) => x !== c))}
                            />
                            <span className="text-[#1A1A1A]">{c}</span>
                          </label>
                        );
                      })}
                    </div>
                    {companyCategories.length > 0 && (
                      <div className="mt-3 text-[10px] text-[#5B5B5B]" data-testid="vendor-category-selected-count">
                        {companyCategories.length} selected
                      </div>
                    )}
                  </div>
                )}
              </div>
              <Field label="Year of Establishment" required>
                <input type="text" inputMode="numeric" data-testid="vendor-year" className={inputCls} value={yearEstablished} onChange={(e) => setYearEstablished(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="e.g. 1998" maxLength={4} />
              </Field>
              <Field label="Registration / Incorporation No." required>
                <input type="text" data-testid="vendor-reg-number" className={inputCls} value={regNumber} onChange={(e) => setRegNumber(e.target.value)} maxLength={80} />
              </Field>
              <Field label="Registration / Incorporation Date" required>
                <input type="date" data-testid="vendor-reg-date" className={inputCls} value={regDate} onChange={(e) => setRegDate(e.target.value)} />
              </Field>
              <Field label="PAN Number" required>
                <input type="text" data-testid="vendor-pan" className={inputCls} value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={20} />
              </Field>
              <Field label="GST Number">
                <input type="text" data-testid="vendor-gst" className={inputCls} value={gst} onChange={(e) => setGst(e.target.value.toUpperCase())} maxLength={20} />
              </Field>
              <Field label="No. of Employees" required>
                <select data-testid="vendor-emp-range" className={inputCls} value={empRange} onChange={(e) => setEmpRange(e.target.value)}>
                  <option value="">— Select —</option>
                  {EMPLOYEE_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Annual Turnover (last FY)" required hint="Approximate range">
                <select data-testid="vendor-annual-turnover" className={inputCls} value={annualTurnover} onChange={(e) => setAnnualTurnover(e.target.value)}>
                  <option value="">— Select —</option>
                  {TURNOVER_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Council of Architecture Registration" hint={isCivilInfra ? "Only for architectural firms" : "Available only when the Type of Company is Civil Construction & Infrastructure"}>
                <input
                  type="text"
                  data-testid="vendor-coa"
                  className={`${inputCls} ${!isCivilInfra ? "bg-[#f5f1e8] text-[#8a8478] cursor-not-allowed" : ""}`}
                  value={isCivilInfra ? coa : ""}
                  disabled={!isCivilInfra}
                  onChange={(e) => setCoa(e.target.value)}
                  maxLength={80}
                />
              </Field>
              <Field label="Institute of Engineers Membership" hint={isCivilInfra ? "Not mandatory" : "Available only when the Type of Company is Civil Construction & Infrastructure"}>
                <input
                  type="text"
                  data-testid="vendor-ie"
                  className={`${inputCls} ${!isCivilInfra ? "bg-[#f5f1e8] text-[#8a8478] cursor-not-allowed" : ""}`}
                  value={isCivilInfra ? ieMembership : ""}
                  disabled={!isCivilInfra}
                  onChange={(e) => setIeMembership(e.target.value)}
                  maxLength={80}
                />
              </Field>
              <div className="md:col-span-2">
                <Field label="Other Professional Registration (if any)">
                  <input type="text" data-testid="vendor-other-reg" className={inputCls} value={otherReg} onChange={(e) => setOtherReg(e.target.value)} maxLength={200} />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Long Description about the Company">
                  <textarea data-testid="vendor-long-desc" className={`${inputCls} min-h-[120px]`} value={longDesc} onChange={(e) => setLongDesc(e.target.value)} maxLength={4000} placeholder="Brief profile: capabilities, key projects, awards, USP …" />
                  <div className="text-[10px] text-[#8a8478] mt-1 text-right">{longDesc.length}/4000</div>
                </Field>
              </div>
            </div>

            <div className="mt-8">
              <h3 className="trust-eyebrow text-[10px] text-[#0D5C63] font-bold inline-flex items-center gap-2">
                <ShieldCheck size={13} /> Required Attachments
              </h3>
              <p className="text-[11px] text-[#8a8478] mt-1">Attach clear PDF copies. Filename must be alphanumeric only (e.g. <code className="bg-[#F5F1E8] px-1 rounded">PANcopy.pdf</code>). Max 500 KB per file.</p>

              <div className="mt-3 grid md:grid-cols-2 gap-3">
                <PdfUpload testid="vendor-file-pan" label="PAN Card Copy" hint="Company / firm PAN" file={panFile} onChange={setPanFile} required />
                <div>
                  <PdfUpload
                    testid="vendor-file-gst"
                    label={gstNotApplicable ? "GST — Not Applicable Declaration" : "GST Registration Certificate"}
                    hint={gstNotApplicable ? "Signed declaration on letterhead" : "Latest GST registration certificate"}
                    file={gstFile}
                    onChange={setGstFile}
                    required
                  />
                  <label className="flex items-start gap-2 mt-2 text-[11px] text-[#3E3E3E] cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-3.5 w-3.5 accent-[#0D5C63]"
                      checked={gstNotApplicable}
                      onChange={(e) => { setGstNotApplicable(e.target.checked); setGstFile(null); }}
                      data-testid="vendor-gst-not-applicable"
                    />
                    <span>
                      <b>GST is not applicable to my firm.</b> I will upload a signed declaration mentioning the same in place of the GST certificate.
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-[#F0F7F5] border border-[#0D5C63]/30 rounded-lg p-5" data-testid="vendor-portal-hint">
              <div className="trust-eyebrow text-[10px] text-[#0D5C63] font-bold inline-flex items-center gap-2">
                <ShieldCheck size={13} /> Additional Documents (post-approval)
              </div>
              <p className="text-xs text-[#3E3E3E] mt-2 leading-relaxed">
                Once your application is <b>approved</b>, you will receive login credentials on your registered email
                to the <b>GSM Vendor Portal</b>. There you can securely upload additional documents — Project Completion
                Certificates, Balance Sheets, Cancelled Cheque and Company/MSME registration.
              </p>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="bg-white border border-[#E9E2D2] rounded-2xl shadow-sm p-6 md:p-8" data-testid="vendor-step-3">
            <div className="trust-eyebrow text-[10px] text-[#D90368] font-bold">Step 3</div>
            <h2 className="trust-serif text-2xl text-[#1A1A1A] mt-1">Review & Submit</h2>

            <ReviewBlock title="Applicant Details">
              <ReviewRow k="Firm Name" v={firm} />
              <ReviewRow k="Type of Applicant" v={applicantType} />
              <ReviewRow k="Registered Address" v={regAddress} />
              <ReviewRow k="Communication Address" v={commAddress} />
              <ReviewRow k="State / City / PIN" v={`${state} / ${city} / ${pin}`} />
              <ReviewRow k="Mobile" v={mobile} />
              <ReviewRow k="Office Phone" v={officePhone} />
              <ReviewRow k="Email" v={email} />
              <ReviewRow k="Website" v={website} />
              <ReviewRow k="Branch Office" v={branchDetails} />
            </ReviewBlock>

            <ReviewBlock title="Organization Details">
              <ReviewRow k="Type / Category" v={`${companyType} / ${(companyCategories || []).join(", ") || "—"}`} />
              <ReviewRow k="Year of Establishment" v={yearEstablished} />
              <ReviewRow k="Registration No. / Date" v={`${regNumber} — ${regDate}`} />
              <ReviewRow k="PAN / GST" v={`${pan} / ${gst || "—"}`} />
              <ReviewRow k="CoA / IE / Other" v={[coa, ieMembership, otherReg].filter(Boolean).join(" · ") || "—"} />
              <ReviewRow k="Employees" v={empRange} />
              <ReviewRow k="Annual Turnover" v={annualTurnover} />
              <ReviewRow k="PAN Card" v={panFile ? panFile.name : "—"} />
              <ReviewRow k={gstNotApplicable ? "GST Declaration" : "GST Certificate"} v={gstFile ? gstFile.name : "—"} />
              <ReviewRow k="Description" v={longDesc || "—"} />
            </ReviewBlock>

            <div className="mt-8 bg-[#F5EFE3] border border-[#E9E2D2] rounded-lg p-5" data-testid="vendor-declaration-block">
              <div className="trust-eyebrow text-[10px] text-[#0D5C63] font-bold inline-flex items-center gap-2">
                <ShieldCheck size={13} /> Declaration
              </div>
              <label className="mt-3 flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={declared}
                  onChange={(e) => setDeclared(e.target.checked)}
                  data-testid="vendor-declaration"
                  className="mt-1 h-5 w-5 accent-[#0D5C63] shrink-0"
                />
                <span className="text-sm md:text-[15px] text-[#1A1A1A] leading-relaxed">
                  I hereby declare that all information furnished is true and correct and I authorize GS Mandal to verify the project and client details provided; I understand that any false or misleading information may result in cancellation of my registration/empanelment, and that GS Mandal&rsquo;s decision regarding empanelment shall be final.
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <Link to=".." relative="path" className="text-xs font-semibold uppercase tracking-wider text-[#0D5C63] hover:text-[#F26419] inline-flex items-center gap-1" data-testid="vendor-back-to-home">
            <ArrowLeft size={13} /> Back to Trust Home
          </Link>
          <div className="flex items-center gap-3">
            {step > 1 && (
              <button type="button" onClick={goBack} data-testid="vendor-btn-prev" className="inline-flex items-center gap-2 bg-white border-2 border-[#0D5C63] text-[#0D5C63] hover:bg-[#0D5C63] hover:text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-full transition-colors">
                <ArrowLeft size={13} /> Previous
              </button>
            )}
            {step < 3 && (
              <button type="button" onClick={goNext} data-testid="vendor-btn-next" className="inline-flex items-center gap-2 bg-[#0D5C63] hover:bg-[#083E43] text-white text-xs font-bold uppercase tracking-wider px-6 py-2.5 rounded-full">
                Next <ArrowRight size={13} />
              </button>
            )}
            {step === 3 && (
              <button type="button" onClick={submit} disabled={saving} data-testid="vendor-btn-submit" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#F26419] to-[#D90368] hover:from-[#D90368] hover:to-[#F26419] text-white text-xs font-bold uppercase tracking-wider px-6 py-2.5 rounded-full shadow-lg shadow-[#F26419]/25 disabled:opacity-60">
                {saving ? <><Loader2 size={13} className="animate-spin" /> Submitting…</> : <><Send size={13} /> Submit Application</>}
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function ReviewBlock({ title, children }) {
  return (
    <div className="mt-6 border border-[#E9E2D2] rounded-lg p-4">
      <div className="trust-eyebrow text-[10px] text-[#0D5C63] font-bold">{title}</div>
      <div className="mt-2 grid md:grid-cols-2 gap-x-6 gap-y-1.5">{children}</div>
    </div>
  );
}

function ReviewRow({ k, v }) {
  return (
    <div className="flex gap-2 text-xs">
      <div className="text-[#5B5B5B] shrink-0 w-40">{k}:</div>
      <div className="text-[#1A1A1A] font-medium break-words">{v || "—"}</div>
    </div>
  );
}

// Simple thank-you page rendered at /vendors/thank-you after successful submit.
export function TrustVendorThankYou() {
  return (
    <div data-testid="trust-vendor-thankyou">
      <PageHero />
      <section className="max-w-3xl mx-auto px-5 py-16 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#0D5C63] text-white">
          <CheckCircle2 size={30} />
        </div>
        <h1 className="trust-serif text-3xl mt-5 text-[#1A1A1A]">Application received</h1>
        <p className="mt-3 text-[#3E3E3E]">Thank you for registering with GS Mandal. Our team will review your submission and reach out at the email address provided. You may close this window.</p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/" className="inline-flex items-center gap-2 bg-[#0D5C63] hover:bg-[#083E43] text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-full">
            <Building2 size={13} /> Trust Home
          </Link>
          <Link to="/vendors/register" className="inline-flex items-center gap-2 bg-white border-2 border-[#F26419] text-[#F26419] hover:bg-[#F26419] hover:text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-full">
            Submit another
          </Link>
        </div>
      </section>
    </div>
  );
}
